// Time entry and display helpers, ported from CONTROL V4 (hm, typeHm, fmtHm,
// fmtDur) so the screen accepts and prints exactly what the design does. They
// format and parse only; every quantity (recorded, gap, deduction, payable) is
// computed by the API's shift-math, never re-derived here.

export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
export const MONS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

// "0730", "07:30", "7.30", "7h30", "7" → minutes; anything else → null.
export function hm(v: unknown): number | null {
  if (v === null || v === undefined) return null
  const s = String(v).trim()
  if (!s) return null
  let m = /^(\d{1,2})[:.h]?(\d{2})$/.exec(s)
  if (!m) {
    m = /^(\d{1,2})$/.exec(s)
    if (m) return +m[1] <= 23 ? +m[1] * 60 : null
    return null
  }
  const h = +m[1]
  const mi = +m[2]
  return h <= 23 && mi <= 59 ? h * 60 + mi : null
}

// As typed: digits only, at most four, the colon placed for the user.
export function typeHm(v: unknown): string {
  const d = String(v || '').replace(/[^0-9]/g, '').slice(0, 4)
  return d.length > 2 ? d.slice(0, d.length - 2) + ':' + d.slice(-2) : d
}

export function fmtHm(n: number): string {
  const v = ((n % 1440) + 1440) % 1440
  return String(Math.floor(v / 60)).padStart(2, '0') + ':' + String(v % 60).padStart(2, '0')
}

export function fmtDur(n: number): string {
  const neg = n < 0
  const a = Math.abs(Math.round(n))
  const h = Math.floor(a / 60)
  const m = a % 60
  return (neg ? '−' : '') + (h ? h + 'h' + (m ? ' ' + m + 'm' : '') : m + 'm')
}

// Local calendar arithmetic: a work date is a wall-calendar day, so it is
// never passed through toISOString (which would shift it by the UTC offset).
export function isoLocal(d: Date): string {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

export function parseIsoLocal(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export function addDaysLocal(iso: string, n: number): string {
  const d = parseIsoLocal(iso)
  d.setDate(d.getDate() + n)
  return isoLocal(d)
}

// The business clock (handoff §9, "Time zone: two zones, one clock for
// money"): "today" runs on the tenant operating time zone "for every user,
// whatever zone they are sitting in". WorkspaceContext sets it from /api/me
// whenever a workspace is adopted. While Dataphyll has not set the tenant
// zone it reads UTC, the clock the API reads that tenant's day in
// (tenant_today, 078), so the browser's own zone is never the answer.
let businessZone: string | null = null

export function setBusinessZone(zone: string | null | undefined) {
  businessZone = typeof zone === 'string' && zone.trim() ? zone.trim() : null
}

export function businessZoneName(): string {
  return businessZone ?? 'UTC'
}

// The wall-clock date and time of an instant in a zone, from the browser's
// tzdata (Intl), never a stored offset.
function wallIn(at: Date, zone: string): { date: string; hm: string } {
  const parts = (tz: string) =>
    Object.fromEntries(
      new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
        .formatToParts(at)
        .map((p) => [p.type, p.value]),
    )
  let p: Record<string, string>
  try {
    p = parts(zone)
  } catch {
    // A zone this browser's tzdata does not know: the API accepted it, so it
    // is newer than the browser. Read it as the unset tenant is read.
    p = parts('UTC')
  }
  return { date: `${p.year}-${p.month}-${p.day}`, hm: `${p.hour}:${p.minute}` }
}

// The tenant's date now (or at `at`), as a work date.
export function todayLocal(at: Date = new Date()): string {
  return wallIn(at, businessZoneName()).date
}

// The tenant's wall clock now (or at `at`), "HH:MM".
export function nowHmBusiness(at: Date = new Date()): string {
  return wallIn(at, businessZoneName()).hm
}

export function dateLabel(iso: string): string {
  const d = parseIsoLocal(iso)
  return DAYS[d.getDay()] + ' ' + d.getDate() + ' ' + MONS[d.getMonth()] + ' ' + d.getFullYear()
}

// "Tue 29 Sep": a date inside a pill or a sentence.
export function shortDateLabel(iso: string): string {
  const d = parseIsoLocal(iso)
  return DAYS[d.getDay()].slice(0, 3) + ' ' + d.getDate() + ' ' + MONS[d.getMonth()].slice(0, 3)
}

// "YYYY-MM-DDTHH:MM" wall-clock label (shift-math) → minutes from the start of
// `baseDate`, so a finish after midnight reads as ≥ 1440.
export function wallMinutes(label: string, baseDate: string): number {
  const day = label.slice(0, 10)
  const t = hm(label.slice(11, 16)) ?? 0
  const days = Math.round((parseIsoLocal(day).getTime() - parseIsoLocal(baseDate).getTime()) / 86400000)
  return days * 1440 + t
}

// An audit timestamp as the design prints it: "28 Sep 14:05".
export function fmtStamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.getDate() + ' ' + MONS[d.getMonth()].slice(0, 3) + ' ' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')
}
